import { useCallback } from 'react';
import { DeleteDDSFile } from 'api/canguard/deleteDDSFile';
import { useNotificationProvider } from 'hooks';
import { useLoading } from 'contextProviders/LoadingBackdropProvider';
import { useScheduleContext } from 'containers/pages/CANguard/context/ScheduleProvider';

/**
 * useDeleteDDSFile - A custom hook to handle deleting DDS files for a given schedule assignment.
 *
 * @param {ScheduleAssignment} patrol - The schedule assignment object.
 * @returns {function} - A function to delete a DDS file by its ID.
 *
 */

const useDeleteDDSFile = (patrol: ScheduleAssignment) => {
	const { notifyError, notifySuccess } = useNotificationProvider();
	const { setIsLoading } = useLoading();
	const { updatePatrolInSchedule } = useScheduleContext();

	const handleDeleteDDSFile = useCallback(
		async (fileId: number) => {
			try {
				setIsLoading(true);
				await DeleteDDSFile(fileId);

				updatePatrolInSchedule({
					...patrol,
					dds_files: patrol.dds_files?.filter(
						(attachment) => attachment.AttachmentID !== fileId,
					),
				});

				notifySuccess('DDS file deleted successfully');
			} catch (error) {
				console.log('Error deleting DDS file', error);
				notifyError('Error deleting DDS file');
			} finally {
				setIsLoading(false);
			}
		},
		[
			patrol,
			notifyError,
			notifySuccess,
			setIsLoading,
			updatePatrolInSchedule,
		],
	);

	return handleDeleteDDSFile;
};

export default useDeleteDDSFile;
